"use client";

import * as React from "react";
import * as Sentry from "@sentry/nextjs";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useRouter } from "next/navigation";
import { useAuth } from "@clerk/nextjs";
import {
  ListFilter,
  NotebookPen,
  Search as SearchIcon,
  StickyNote,
  X,
} from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import {
  getAlgoliaSearchCredentials,
  ALGOLIA_POSTS_INDEX_NAME,
  ALGOLIA_PROBLEMS_INDEX_NAME,
  type AlgoliaSearchResultTarget,
  type AlgoliaSearchKeyTarget,
  type ErrorNoteSearchHit,
  type QuerySuggestionHit,
  type SearchHit,
} from "@/lib/algolia";
import {
  configureAlgoliaInsightsCredentials,
  sendSearchResultClicked,
  stashSearchClickForConversion,
} from "@/lib/algolia-insights";
import {
  SUBJECT_LABELS as NOTE_SUBJECT_LABELS,
  type NoteListItem,
} from "@/types/note";

type SearchProps = {
  interClass?: string;
  /** "icon": 아이콘 전용 버튼(사이드바 아이콘 레일용). 같은 검색 다이얼로그 사용. */
  variant?: "default" | "icon";
};

type SearchGroupPayload = {
  hits?: SearchHit[];
  nbHits?: number;
};

type SuggestionGroupPayload = {
  hits?: QuerySuggestionHit[];
  nbHits?: number;
  unavailable?: true;
};

type ErrorNoteGroupPayload = {
  hits?: ErrorNoteSearchHit[];
  nbHits?: number;
  unavailable?: true;
};

type SearchApiPayload = {
  query: string;
  target?: AlgoliaSearchResultTarget;
  posts?: SearchGroupPayload;
  problems?: SearchGroupPayload;
  errorNotes?: ErrorNoteGroupPayload;
  suggestions?: SuggestionGroupPayload;
};

const MIN_SEARCH_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 350;
const LEADING_SEARCH_RESULT_COUNT = 2;
const PERSONAL_NOTE_RESULT_LIMIT = 4;
const MISSING_SEARCH_RESULT_PAGE_MESSAGE =
  "관련 페이지가 존재하지 않습니다. 다른 결과를 선택해주세요.";
const CONCEPT_RESULT_SECTION_CLASS_NAME =
  "mx-2 rounded-md border border-amber-200/70 bg-amber-50/80 dark:border-amber-900/60 dark:bg-amber-950/25 [&_[cmdk-group-heading]]:text-amber-800 dark:[&_[cmdk-group-heading]]:text-amber-300";
const PROBLEM_RESULT_SECTION_CLASS_NAME =
  "mx-2 rounded-md border border-blue-200/70 bg-blue-50/80 dark:border-blue-900/60 dark:bg-blue-950/25 [&_[cmdk-group-heading]]:text-blue-800 dark:[&_[cmdk-group-heading]]:text-blue-300";
const ERROR_NOTE_RESULT_SECTION_CLASS_NAME =
  "mx-2 rounded-md border border-rose-200/70 bg-rose-50/80 dark:border-rose-900/60 dark:bg-rose-950/25 [&_[cmdk-group-heading]]:text-rose-800 dark:[&_[cmdk-group-heading]]:text-rose-300";
const NOTE_RESULT_SECTION_CLASS_NAME =
  "mx-2 rounded-md border border-emerald-200/70 bg-emerald-50/80 dark:border-emerald-900/60 dark:bg-emerald-950/25 [&_[cmdk-group-heading]]:text-emerald-800 dark:[&_[cmdk-group-heading]]:text-emerald-300";
const NEUTRAL_RESULT_SECTION_CLASS_NAME = "mx-2";

type NotesListApiPayload = {
  notes?: NoteListItem[];
  error?: string;
};

type SearchResultEntry = {
  hit: SearchHit;
  position: number;
  sourceOrder: number;
};

type SearchResultSection = {
  key: string;
  heading: string;
  entries: SearchResultEntry[];
  className: string;
};

type SearchFilterOption<TValue extends string = string> = {
  value: TValue;
  label: string;
};

const RESULT_TARGET_OPTIONS: SearchFilterOption<AlgoliaSearchResultTarget>[] = [
  { value: "all", label: "전체" },
  { value: "problems", label: "문제" },
];

type SearchFilterGroup = {
  label: string;
  options: SearchFilterOption[];
};

const SUBJECT_ALL_OPTION: SearchFilterOption = { value: "all", label: "과목" };

const SUBJECT_OPTION_GROUPS: SearchFilterGroup[] = [
  {
    label: "코딩",
    options: [
      { value: "c", label: "C" },
      { value: "java", label: "Java" },
      { value: "python", label: "Python" },
      { value: "sql", label: "SQL" },
    ],
  },
  {
    label: "이론",
    options: [
      { value: "db", label: "DB" },
      { value: "network-os", label: "네트워크/OS" },
      { value: "security-newtech", label: "보안/신기술" },
      { value: "sw-design", label: "SW 설계" },
      { value: "sw-dev", label: "SW 개발" },
    ],
  },
];

const SUBJECT_OPTIONS = SUBJECT_OPTION_GROUPS.flatMap((group) => group.options);
const SUBJECT_LABELS = new Map(
  [SUBJECT_ALL_OPTION, ...SUBJECT_OPTIONS].map((option) => [
    option.value,
    option.label,
  ])
);

const PROBLEM_SCOPE_TO_FORUM_SUBJECT: Record<string, string> = {
  sql: "programming",
  logicDBdesign: "db",
  physicalDBdesign: "db",
  transaction: "db",
  test: "sw-dev",
  interface: "sw-dev",
  integrationImpl: "sw-dev",
  appDesign: "sw-design",
  uml: "sw-design",
  ui: "sw-design",
  methodology: "sw-design",
  memoryProcess: "network-os",
  ip: "network-os",
  network7Layer: "network-os",
  shellScript: "network-os",
  attackTypes: "security-newtech",
  cryptographyAlgorithm: "security-newtech",
  securityFeature: "security-newtech",
  newTech: "security-newtech",
  java: "programming",
  python: "programming",
  c: "programming",
};

const coerceSearchText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value == null) return "";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const toPlainSearchText = (value: unknown): string =>
  coerceSearchText(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getPersonalNoteSearchText = (note: NoteListItem): string =>
  [
    note.content,
    note.pageTitle,
    NOTE_SUBJECT_LABELS[note.subject] ?? note.subject,
  ]
    .map(toPlainSearchText)
    .filter(Boolean)
    .join("\n")
    .toLowerCase();

const getPersonalNotePreview = (content: string): string => {
  const text = toPlainSearchText(content);
  if (!text) return "내용 없는 필기";
  return text.length > 150 ? `${text.slice(0, 150)}…` : text;
};

export default function Search({ interClass = "", variant = "default" }: SearchProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [resultTarget, setResultTarget] =
    React.useState<AlgoliaSearchResultTarget>("all");
  const [subjectFilter, setSubjectFilter] = React.useState("all");
  const [postResults, setPostResults] = React.useState<SearchHit[]>([]);
  const [problemResults, setProblemResults] = React.useState<SearchHit[]>([]);
  const [errorNoteResults, setErrorNoteResults] = React.useState<
    ErrorNoteSearchHit[]
  >([]);
  const [suggestions, setSuggestions] = React.useState<QuerySuggestionHit[]>([]);
  const [personalNotes, setPersonalNotes] = React.useState<NoteListItem[]>([]);
  const [hasLoadedPersonalNotes, setHasLoadedPersonalNotes] =
    React.useState(false);
  const [isLoadingPersonalNotes, setIsLoadingPersonalNotes] =
    React.useState(false);
  const [onlyErrorNotes, setOnlyErrorNotes] = React.useState(false);
  const [onlyPersonalNotes, setOnlyPersonalNotes] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [navigationError, setNavigationError] = React.useState<string | null>(
    null
  );
  const skipScrollRestoreRef = React.useRef(false);
  const personalNotesRequestRef = React.useRef(false);
  const router = useRouter();
  const { isSignedIn, userId } = useAuth();
  const onlyErrorNotesId = React.useId();
  const onlyPersonalNotesId = React.useId();
  const trimmedQuery = query.trim();
  const hasSearchQuery = trimmedQuery.length >= MIN_SEARCH_QUERY_LENGTH;
  const hasActiveFilters =
    resultTarget !== "all" ||
    subjectFilter !== "all" ||
    onlyErrorNotes ||
    onlyPersonalNotes;
  const showSearchFilters = hasSearchQuery || hasActiveFilters;
  const showPersonalNoteResults =
    Boolean(isSignedIn) &&
    hasSearchQuery &&
    (onlyPersonalNotes ||
      (resultTarget === "all" &&
        subjectFilter === "all" &&
        !onlyErrorNotes));

  React.useEffect(() => {
    if (isSignedIn) return;

    queueMicrotask(() => {
      if (onlyErrorNotes) {
        setOnlyErrorNotes(false);
      }
      if (onlyPersonalNotes) {
        setOnlyPersonalNotes(false);
      }
    });
  }, [isSignedIn, onlyErrorNotes, onlyPersonalNotes]);

  React.useEffect(() => {
    if (isSignedIn) return;

    personalNotesRequestRef.current = false;
    queueMicrotask(() => {
      setPersonalNotes([]);
      setHasLoadedPersonalNotes(false);
      setIsLoadingPersonalNotes(false);
    });
  }, [isSignedIn]);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  React.useEffect(() => {
    skipScrollRestoreRef.current = false;
  }, [open]);

  useBodyScrollLock(open, {
    shouldRestoreScroll: () => !skipScrollRestoreRef.current,
  });

  React.useEffect(() => {
    const trimmedQuery = query.trim();
    const controller = new AbortController();
    let ignore = false;

    if (onlyPersonalNotes) {
      queueMicrotask(() => {
        setPostResults([]);
        setProblemResults([]);
        setErrorNoteResults([]);
        setSuggestions([]);
        setIsLoading(false);
      });
      return () => {
        ignore = true;
        controller.abort();
      };
    }

    const performSearch = async () => {
      try {
        const params = new URLSearchParams({ q: trimmedQuery });
        if (resultTarget !== "all") params.set("target", resultTarget);
        if (subjectFilter !== "all") {
          params.set("language", subjectFilter);
        }
        if (onlyErrorNotes) {
          params.set("onlyErrorNotes", "true");
        }

        const response = await fetch(
          `/api/algolia/search?${params.toString()}`,
          {
            cache: "no-store",
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          throw new Error("Algolia 검색에 실패했습니다.");
        }

        const payload = (await response.json()) as SearchApiPayload;
        if (ignore) return;

        setPostResults(payload.posts?.hits ?? []);
        setProblemResults(payload.problems?.hits ?? []);
        setErrorNoteResults(payload.errorNotes?.hits ?? []);
        setSuggestions(payload.suggestions?.hits ?? []);
      } catch (error) {
        if (ignore || (error as Error).name === "AbortError") return;
        setPostResults([]);
        setProblemResults([]);
        setErrorNoteResults([]);
        setSuggestions([]);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(() => {
      if (trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
        setPostResults([]);
        setProblemResults([]);
        setErrorNoteResults([]);
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      setPostResults([]);
      setProblemResults([]);
      setErrorNoteResults([]);
      setSuggestions([]);
      setIsLoading(true);
      void performSearch();
    }, hasSearchQuery ? SEARCH_DEBOUNCE_MS : 0);

    return () => {
      ignore = true;
      controller.abort();
      clearTimeout(debounceTimer);
    };
  }, [
    hasSearchQuery,
    onlyErrorNotes,
    onlyPersonalNotes,
    query,
    resultTarget,
    subjectFilter,
  ]);

  React.useEffect(() => {
    if (
      !showPersonalNoteResults ||
      hasLoadedPersonalNotes ||
      personalNotesRequestRef.current
    ) {
      return;
    }

    const controller = new AbortController();
    let ignore = false;

    const loadPersonalNotes = async () => {
      personalNotesRequestRef.current = true;
      setIsLoadingPersonalNotes(true);
      try {
        const response = await fetch("/api/notes/list", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("필기 목록 조회에 실패했습니다.");
        }
        const payload = (await response.json()) as NotesListApiPayload;
        if (ignore) return;

        setPersonalNotes(payload.notes ?? []);
        setHasLoadedPersonalNotes(true);
      } catch (error) {
        if (ignore || (error as Error).name === "AbortError") return;
        setPersonalNotes([]);
        setHasLoadedPersonalNotes(true);
      } finally {
        if (!ignore) {
          setIsLoadingPersonalNotes(false);
        }
        personalNotesRequestRef.current = false;
      }
    };

    void loadPersonalNotes();

    return () => {
      ignore = true;
      controller.abort();
      personalNotesRequestRef.current = false;
    };
  }, [hasLoadedPersonalNotes, showPersonalNoteResults]);

  const results = React.useMemo(
    () => [...postResults, ...problemResults],
    [postResults, problemResults]
  );

  const personalNoteResults = React.useMemo(() => {
    if (!showPersonalNoteResults) return [];
    const normalizedQuery = trimmedQuery.toLowerCase();
    return personalNotes
      .filter((note) =>
        getPersonalNoteSearchText(note).includes(normalizedQuery)
      )
      .slice(
        0,
        onlyPersonalNotes ? PERSONAL_NOTE_RESULT_LIMIT * 2 : PERSONAL_NOTE_RESULT_LIMIT
      );
  }, [onlyPersonalNotes, personalNotes, showPersonalNoteResults, trimmedQuery]);

  const hasAnySearchResults =
    results.length > 0 ||
    errorNoteResults.length > 0 ||
    personalNoteResults.length > 0;
  const isWaitingForPersonalNotes =
    showPersonalNoteResults && isLoadingPersonalNotes;

  const resultSections = React.useMemo<SearchResultSection[]>(() => {
    const toEntries = (
      hits: SearchHit[],
      sourceOrder: number
    ): SearchResultEntry[] =>
      hits.map((hit, index) => ({
        hit,
        position: hit.__position ?? index + 1,
        sourceOrder,
      }));

    const conceptEntries = toEntries(postResults, 0);
    const problemEntries = toEntries(problemResults, 1);

    if (conceptEntries.length === 0) {
      return problemEntries.length > 0
        ? [
            {
              key: "problems",
              heading: "문제",
              entries: problemEntries,
              className: PROBLEM_RESULT_SECTION_CLASS_NAME,
            },
          ]
        : [];
    }

    if (problemEntries.length === 0) {
      return [
        {
          key: "concepts",
          heading: "개념",
          entries: conceptEntries,
          className: CONCEPT_RESULT_SECTION_CLASS_NAME,
        },
      ];
    }

    const remainingEntries = [
      ...conceptEntries.slice(LEADING_SEARCH_RESULT_COUNT),
      ...problemEntries.slice(LEADING_SEARCH_RESULT_COUNT),
    ].sort(
      (a, b) => a.position - b.position || a.sourceOrder - b.sourceOrder
    );

    return [
      {
        key: "leading-concepts",
        heading: "개념",
        entries: conceptEntries.slice(0, LEADING_SEARCH_RESULT_COUNT),
        className: CONCEPT_RESULT_SECTION_CLASS_NAME,
      },
      {
        key: "leading-problems",
        heading: "문제",
        entries: problemEntries.slice(0, LEADING_SEARCH_RESULT_COUNT),
        className: PROBLEM_RESULT_SECTION_CLASS_NAME,
      },
      {
        key: "remaining-results",
        heading: "관련 결과",
        entries: remainingEntries,
        className: NEUTRAL_RESULT_SECTION_CLASS_NAME,
      },
    ].filter((section) => section.entries.length > 0);
  }, [postResults, problemResults]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setNavigationError(null);
    }
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    setNavigationError(null);
  };

  const resetFilters = () => {
    setResultTarget("all");
    setSubjectFilter("all");
    setOnlyErrorNotes(false);
    setOnlyPersonalNotes(false);
    setNavigationError(null);
  };

  const handleResultTargetChange = (value: string) => {
    const nextTarget = value as AlgoliaSearchResultTarget;
    setResultTarget(nextTarget);
    setOnlyPersonalNotes(false);
    if (nextTarget !== "problems") {
      setSubjectFilter("all");
      setOnlyErrorNotes(false);
    }
    setNavigationError(null);
  };

  const handleSubjectFilterChange = (value: string) => {
    setSubjectFilter(value);
    setOnlyPersonalNotes(false);
    if (value !== "all") {
      setResultTarget("problems");
    }
    setNavigationError(null);
  };

  const handleOnlyErrorNotesChange = (checked: boolean) => {
    setOnlyErrorNotes(checked);
    if (checked) {
      setResultTarget("problems");
      setOnlyPersonalNotes(false);
    }
    setNavigationError(null);
  };

  const handleOnlyPersonalNotesChange = (checked: boolean) => {
    setOnlyPersonalNotes(checked);
    if (checked) {
      setResultTarget("all");
      setSubjectFilter("all");
      setOnlyErrorNotes(false);
    }
    setNavigationError(null);
  };

  const sendClickInsightsForHit = React.useCallback(
    (
      hit: SearchHit,
      ctx: { index: string; queryID: string; objectID: string },
      position: number
    ): void => {
      const target: AlgoliaSearchKeyTarget =
        hit.__indexName === ALGOLIA_PROBLEMS_INDEX_NAME ? "problems" : "posts";

      void getAlgoliaSearchCredentials(target)
        .then((credentials) => {
          configureAlgoliaInsightsCredentials({
            appId: credentials.appId,
            apiKey: credentials.apiKey,
          });
          sendSearchResultClicked({ ...ctx, position }, userId);
        })
        .catch(() => {});
    },
    [userId]
  );

  const getProblemId = (hit: SearchHit) =>
    hit.problemId ??
    (hit.__indexName === ALGOLIA_PROBLEMS_INDEX_NAME ? hit.objectID : undefined);

  const getFallbackProblemUrl = (hit: SearchHit) => {
    const problemId = getProblemId(hit);
    if (!problemId) return null;

    const scope = hit.scope ?? problemId.split("_")[0];
    const subject = PROBLEM_SCOPE_TO_FORUM_SUBJECT[scope];
    if (!subject) return null;

    return `/forum/jcg/${subject}/${scope}/${problemId}#problem-${problemId}`;
  };

  const getTargetUrl = (hit: SearchHit) => {
    if (hit.url) return hit.url;
    if (hit.slug) {
      const slugPath = hit.slug.startsWith("/") ? hit.slug : `/${hit.slug}`;
      return `${slugPath}?highlight=${encodeURIComponent(query.trim())}`;
    }
    return getFallbackProblemUrl(hit);
  };

  const reportMissingSearchResultPage = (hit: SearchHit) => {
    Sentry.captureMessage("Algolia search result has no navigable page", {
      level: "error",
      tags: {
        component: "Search",
        indexName: hit.__indexName ?? "unknown",
        recordType: hit.recordType ?? "unknown",
      },
      extra: {
        objectID: hit.objectID,
        problemId: getProblemId(hit),
        slug: hit.slug,
        url: hit.url,
        scope: hit.scope,
        subject: hit.subject,
        query: trimmedQuery,
      },
    });
  };

  const getTargetPathname = (targetUrl: string) => {
    try {
      return new URL(targetUrl, window.location.origin).pathname;
    } catch {
      return targetUrl.split(/[?#]/)[0] || "/";
    }
  };

  const handleSelect = (hit: SearchHit, position: number) => {
    const targetUrl = getTargetUrl(hit);
    if (!targetUrl) {
      reportMissingSearchResultPage(hit);
      setNavigationError(MISSING_SEARCH_RESULT_PAGE_MESSAGE);
      return;
    }

    setNavigationError(null);
    const indexName = hit.__indexName ?? ALGOLIA_POSTS_INDEX_NAME;
    const hitQueryID = hit.__queryID;

    // 검색→클릭 흐름을 queryID로 묶어 Insights에 전송 (JCG-2181).
    // queryID는 clickAnalytics 응답에서만 오므로, 없으면 이벤트는 건너뛰고 이동만 한다.
    if (hitQueryID) {
      const ctx = {
        index: indexName,
        queryID: hitQueryID,
        objectID: hit.objectID,
      };
      // 목적지 경로 도달 시 전환 이벤트로 이어가기 위해 컨텍스트 보존
      stashSearchClickForConversion(ctx, getTargetPathname(targetUrl), userId);
      // position은 1-based(첫 결과 = 1)
      sendClickInsightsForHit(hit, ctx, hit.__position ?? position);
    }
    skipScrollRestoreRef.current = true;
    setOpen(false);
    router.push(targetUrl);
  };

  const handlePersonalNoteSelect = (note: NoteListItem) => {
    setNavigationError(null);
    skipScrollRestoreRef.current = true;
    setOpen(false);
    router.push(note.pagePath || "/my/notes");
  };

  const handleErrorNoteSelect = (hit: ErrorNoteSearchHit) => {
    const params = new URLSearchParams({ problemId: hit.problemId });
    if (hit.noteId) params.set("noteId", hit.noteId);
    if (trimmedQuery) params.set("search", trimmedQuery);
    const targetUrl = `/my/error-notes?${params.toString()}`;

    setNavigationError(null);
    skipScrollRestoreRef.current = true;
    setOpen(false);
    router.push(targetUrl);
  };

  const handleSuggestionSelect = (suggestion: string) => {
    setQuery(suggestion);
    setNavigationError(null);
  };

  const renderFilterSelect = ({
    value,
    onValueChange,
    options,
    optionGroups,
    ariaLabel,
    className,
  }: {
    value: string;
    onValueChange: (value: string) => void;
    options?: SearchFilterOption[];
    optionGroups?: SearchFilterGroup[];
    ariaLabel: string;
    className: string;
  }) => (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        size="sm"
        className={`h-8 min-w-0 bg-background text-xs ${className}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        {options?.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        {optionGroups?.map((group) => (
          <SelectGroup key={group.label}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );

  const getHighlightedText = (highlightResult?: { value: string }) => {
    if (!highlightResult) return "";
    return highlightResult.value;
  };

  const escapeRegExp = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const stripHighlightTags = (value: string) => value.replace(/<[^>]*>/g, "");

  const getProblemSubjectLabel = (hit: SearchHit) =>
    hit.subject ? SUBJECT_LABELS.get(hit.subject) ?? hit.subject : null;

  const getProblemDisplayIds = (hit: SearchHit) =>
    Array.from(
      new Set(
        [getProblemId(hit), hit.objectID].filter(
          (id): id is string => Boolean(id)
        )
      )
    );

  const removeProblemIdsFromText = (value: string, ids: string[]) => {
    let nextValue = value;
    ids.forEach((id) => {
      nextValue = nextValue.replace(new RegExp(escapeRegExp(id), "g"), "");
    });

    return nextValue
      .replace(/\s*·\s*·\s*/g, " · ")
      .replace(/^\s*[·|,;:/-]\s*/, "")
      .replace(/\s*[·|,;:/-]\s*$/, "")
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  const sanitizeProblemDisplayText = (
    hit: SearchHit,
    value: string,
    { stripTags = true }: { stripTags?: boolean } = {}
  ) => {
    if (hit.__indexName !== ALGOLIA_PROBLEMS_INDEX_NAME) return value;

    const ids = getProblemDisplayIds(hit);
    if (ids.length === 0) return value;

    const sanitized = removeProblemIdsFromText(
      stripTags ? stripHighlightTags(value) : value,
      ids
    );

    const stillContainsId = ids.some((id) =>
      stripHighlightTags(sanitized).includes(id)
    );
    if (!stillContainsId) return sanitized;

    return removeProblemIdsFromText(stripHighlightTags(value), ids);
  };

  const getHitTitle = (hit: SearchHit) => {
    const title = getHighlightedText(hit._highlightResult?.title) || hit.title;
    const sanitizedTitle = title ? sanitizeProblemDisplayText(hit, title) : "";
    if (sanitizedTitle) return sanitizedTitle;
    if (hit.__indexName === ALGOLIA_PROBLEMS_INDEX_NAME) {
      const subjectLabel = getProblemSubjectLabel(hit);
      return subjectLabel ? `${subjectLabel} 문제` : "문제";
    }
    return hit.objectID;
  };

  const getHitDescription = (hit: SearchHit) => {
    const description =
      getHighlightedText(hit._highlightResult?.description) || hit.description || "";
    return description ? sanitizeProblemDisplayText(hit, description) : "";
  };

  const getProblemMeta = (hit: SearchHit) =>
    [getProblemSubjectLabel(hit)].filter(Boolean);

  const renderResultItem = ({ hit, position }: SearchResultEntry) => {
    const isProblem = hit.__indexName === ALGOLIA_PROBLEMS_INDEX_NAME;
    const typeLabel = isProblem ? "문제" : "개념";
    const meta = isProblem ? getProblemMeta(hit) : [hit.category].filter(Boolean);
    const title = getHitTitle(hit);
    const description = getHitDescription(hit);
    const snippet =
      hit._snippetResult?.content?.value ||
      getHighlightedText(hit._highlightResult?.content) ||
      (hit.content ? `${hit.content.substring(0, 150)}…` : "");
    const contentSnippet = snippet
      ? sanitizeProblemDisplayText(hit, snippet, { stripTags: false })
      : "";

    return (
      <CommandItem
        key={`${hit.__indexName ?? ALGOLIA_POSTS_INDEX_NAME}-${hit.objectID}`}
        value={`${query} ${title} ${description}`}
        onSelect={() => handleSelect(hit, position)}
      >
        <div className="w-full min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {typeLabel}
            </span>
            {meta.length > 0 && (
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                {meta.join(" · ")}
              </span>
            )}
          </div>
          <h3
            className="line-clamp-2 text-sm font-medium text-foreground sm:text-base"
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          {description && (
            <p
              className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          )}
          {contentSnippet && (
            <p
              className="mt-1 line-clamp-2 text-xs italic text-muted-foreground"
              dangerouslySetInnerHTML={{
                __html: contentSnippet,
              }}
            />
          )}
        </div>
      </CommandItem>
    );
  };

  const renderPersonalNoteItem = (note: NoteListItem) => {
    const subjectLabel = NOTE_SUBJECT_LABELS[note.subject] ?? note.subject;
    const preview = getPersonalNotePreview(note.content);

    return (
      <CommandItem
        key={`personal-note-${note.id}`}
        value={`${query} ${note.pageTitle} ${subjectLabel} ${preview}`}
        onSelect={() => handlePersonalNoteSelect(note)}
      >
        <div className="w-full min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              필기
            </span>
            {subjectLabel && (
              <span className="min-w-0 truncate text-xs text-muted-foreground">
                {subjectLabel}
              </span>
            )}
          </div>
          <div className="flex min-w-0 items-start gap-2">
            <NotebookPen
              className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 text-sm font-medium text-foreground sm:text-base">
                {note.pageTitle || "제목 없는 페이지"}
              </h3>
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">
                {preview}
              </p>
            </div>
          </div>
        </div>
      </CommandItem>
    );
  };

  const renderErrorNoteItem = (hit: ErrorNoteSearchHit) => {
    const title = hit.title || "내 오답노트";
    const preview = getPersonalNotePreview(hit.content);

    return (
      <CommandItem
        key={hit.objectID}
        value={`${query} ${title} ${hit.problemId} ${preview}`}
        onSelect={() => handleErrorNoteSelect(hit)}
      >
        <div className="w-full min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              오답노트
            </span>
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {hit.problemId}
            </span>
          </div>
          <div className="flex min-w-0 items-start gap-2">
            <StickyNote
              className="mt-0.5 h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 text-sm font-medium text-foreground sm:text-base">
                {title}
              </h3>
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">
                {preview}
              </p>
            </div>
          </div>
        </div>
      </CommandItem>
    );
  };

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-[42px] w-[42px] rounded-xl text-muted-foreground"
          onClick={() => setOpen(true)}
          aria-label="검색 열기"
          title="검색"
        >
          <SearchIcon className="h-[18px] w-[18px]" aria-hidden="true" />
          <span className="sr-only">검색</span>
        </Button>
      ) : (
        <Button
          variant="outline"
          className="flex h-[36px] w-10 items-center justify-center px-0 sm:w-[180px] sm:justify-between sm:px-3"
          onClick={() => setOpen(true)}
        >
          <div className="flex items-center">
            <SearchIcon
              className="w-4 h-4 text-gray-500 dark:text-gray-400 sm:mr-2"
              aria-hidden="true"
            />
            <span className="sr-only sm:hidden">검색</span>
            <span
              className={`hidden text-sm text-gray-500 dark:text-gray-400 sm:inline ${interClass}`}
            >
              검색
            </span>
          </div>
          <kbd className="hidden sm:inline-flex pointer-events-none h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      )}
      <CommandDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="검색"
        description="검색어를 입력해 개념, 문제, 필기를 찾습니다."
        className="top-[10%] translate-y-0 max-h-[80dvh] flex flex-col overscroll-contain [&_[data-slot=command]]:min-h-0"
      >
        <CommandInput
          aria-label="검색어"
          name="site-search"
          autoComplete="off"
          enterKeyHint="search"
          placeholder="검색어를 입력하세요…"
          value={query}
          onValueChange={handleQueryChange}
        />
        {showSearchFilters && (
          <div className="border-b bg-muted/30 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <ListFilter
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              {renderFilterSelect({
                value: resultTarget,
                onValueChange: handleResultTargetChange,
                options: RESULT_TARGET_OPTIONS,
                ariaLabel: "검색 범위",
                className: "w-[78px]",
              })}
              {renderFilterSelect({
                value: subjectFilter,
                onValueChange: handleSubjectFilterChange,
                options: [SUBJECT_ALL_OPTION],
                optionGroups: SUBJECT_OPTION_GROUPS,
                ariaLabel: "문제 과목",
                className: "w-[118px]",
              })}
              {isSignedIn && (
                <>
                  <label
                    htmlFor={onlyErrorNotesId}
                    className={`inline-flex h-8 min-w-0 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors ${
                      onlyErrorNotes
                        ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                    } focus-within:ring-2 focus-within:ring-amber-500 focus-within:ring-offset-2 focus-within:ring-offset-background`}
                  >
                    <Checkbox
                      id={onlyErrorNotesId}
                      checked={onlyErrorNotes}
                      onCheckedChange={(checked) =>
                        handleOnlyErrorNotesChange(checked === true)
                      }
                      aria-label="내 오답노트에 담긴 문제만 검색"
                    />
                    <StickyNote
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden="true"
                    />
                    <span className="hidden sm:inline">내 오답노트만</span>
                    <span className="sm:hidden">오답노트</span>
                  </label>
                  <label
                    htmlFor={onlyPersonalNotesId}
                    className={`inline-flex h-8 min-w-0 cursor-pointer items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors ${
                      onlyPersonalNotes
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                        : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                    } focus-within:ring-2 focus-within:ring-emerald-500 focus-within:ring-offset-2 focus-within:ring-offset-background`}
                  >
                    <Checkbox
                      id={onlyPersonalNotesId}
                      checked={onlyPersonalNotes}
                      onCheckedChange={(checked) =>
                        handleOnlyPersonalNotesChange(checked === true)
                      }
                      aria-label="내 필기만 검색"
                    />
                    <NotebookPen
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden="true"
                    />
                    <span className="hidden sm:inline">내 필기만</span>
                    <span className="sm:hidden">필기</span>
                  </label>
                </>
              )}
              {hasActiveFilters && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground"
                  onClick={resetFilters}
                  aria-label="검색 필터 초기화"
                  title="필터 초기화"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
        )}
        <CommandList className="min-h-0 max-h-[60dvh] flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
          {isLoading && <CommandEmpty>검색 중…</CommandEmpty>}
          {!isLoading &&
            isWaitingForPersonalNotes &&
            !hasAnySearchResults &&
            suggestions.length === 0 && (
              <CommandEmpty>검색 중…</CommandEmpty>
          )}
          {!isLoading &&
            hasSearchQuery &&
            !isWaitingForPersonalNotes &&
            !hasAnySearchResults &&
            suggestions.length === 0 && (
              <CommandEmpty>검색 결과가 없습니다.</CommandEmpty>
          )}

          {navigationError && (
            <div
              role="alert"
              aria-live="polite"
              className="mx-3 mb-2 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive"
            >
              {navigationError}
            </div>
          )}

          {/* 자동완성 제안어 */}
          {suggestions.length > 0 && (
            <CommandGroup heading="추천 검색어">
              {suggestions.map((suggestion, index) => (
                <CommandItem
                  key={suggestion.objectID || `suggestion-${index}`}
                  value={suggestion.query}
                  onSelect={() => handleSuggestionSelect(suggestion.query)}
                  className="text-sm text-muted-foreground"
                >
                  <SearchIcon className="w-3 h-3 mr-2" aria-hidden="true" />
                  {suggestion.query}
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* 개인 오답노트 결과: Firestore의 본인 오답노트 본문만 서버에서 검색한다. */}
          {errorNoteResults.length > 0 && (
            <>
              {suggestions.length > 0 && (
                <CommandSeparator className="mx-2 my-1" />
              )}
              <CommandGroup
                heading="내 오답노트"
                className={ERROR_NOTE_RESULT_SECTION_CLASS_NAME}
              >
                {errorNoteResults.map((hit) => renderErrorNoteItem(hit))}
              </CommandGroup>
            </>
          )}

          {/* 개인 필기 결과: 클라이언트에서 본인 노트만 필터링한다. */}
          {personalNoteResults.length > 0 && (
            <>
              {(suggestions.length > 0 || errorNoteResults.length > 0) && (
                <CommandSeparator className="mx-2 my-1" />
              )}
              <CommandGroup
                heading="내 필기"
                className={NOTE_RESULT_SECTION_CLASS_NAME}
              >
                {personalNoteResults.map((note) =>
                  renderPersonalNoteItem(note)
                )}
              </CommandGroup>
            </>
          )}

          {/* 검색 결과 */}
          {hasSearchQuery &&
            resultSections.map((section, index) => (
              <React.Fragment key={section.key}>
                {(suggestions.length > 0 ||
                  errorNoteResults.length > 0 ||
                  personalNoteResults.length > 0 ||
                  index > 0) && (
                  <CommandSeparator className="mx-2 my-1" />
                )}
                <CommandGroup
                  heading={section.heading}
                  className={section.className}
                >
                  {section.entries.map((entry) => renderResultItem(entry))}
                </CommandGroup>
              </React.Fragment>
            ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
